import axios from "axios";
import { getAuth, signOut } from "firebase/auth";
import { getViewAsUserId, clearViewAsUserId } from "../utils/viewAs";

// The backend's machine-readable `detail` for an archived account (see
// firebase_auth.ACCOUNT_ARCHIVED_DETAIL). It has to be distinguishable from an
// ordinary permission 403, which is a normal thing to hit and must NOT sign
// anyone out.
const ACCOUNT_ARCHIVED_DETAIL = "account_archived";
// A partner company session that predates a password change (or carries no
// tech name) — services/partner_auth_service.SESSION_ENDED_DETAIL.
const PARTNER_SESSION_ENDED_DETAIL = "partner_session_ended";

// Read by the login page to explain the ejection. sessionStorage, not state:
// the sign-out unmounts everything on its way past.
export const SIGNOUT_REASON_KEY = "signout_reason";

// Guards against a burst of in-flight requests each triggering their own
// sign-out and reload.
let ejecting = false;

/**
 * Sign out a user whose account was archived while they had the app open.
 *
 * ⚠️ Why the interceptor has to do this rather than waiting for a refetch:
 * `AuthContext` caches /me in sessionStorage and, when `fetchMe()` fails, it
 * logs the error and KEEPS the cached value — so an archived user's open tab
 * would otherwise carry on rendering indefinitely with a stale identity. The
 * backend cannot help here either: nothing in this repo writes Firebase user
 * state, so a signed-in browser keeps minting valid ID tokens from its refresh
 * token. Ejecting client-side on the 403 is the only thing that closes the tab.
 */
async function ejectArchivedUser(
  reason = "Your access has been removed. Contact a system administrator if you think this is a mistake."
) {
  if (ejecting) return;
  ejecting = true;
  try {
    sessionStorage.setItem(SIGNOUT_REASON_KEY, reason);
    sessionStorage.removeItem("user"); // AuthContext's cached /me
    clearViewAsUserId(); // don't carry a masquerade into the next sign-in
  } catch {
    /* sessionStorage throws in Safari private mode — never block the sign-out */
  }
  try {
    await signOut(getAuth());
  } catch {
    /* ignore — the reload below is what actually clears the app */
  }
  window.location.assign("/");
}

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Build a query string; arrays become repeated params (status=a&status=b —
// what FastAPI List[str] expects), null/empty values are dropped.
export function toQuery(params = {}) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    if (Array.isArray(v)) {
      v.forEach((x) => x != null && x !== "" && usp.append(k, x));
    } else {
      usp.append(k, v);
    }
  }
  return usp.toString();
}

// Triggers a browser save dialog for a downloaded blob response (a PDF, a
// zip, ...). Shared so a third/fourth download consumer doesn't duplicate
// this again — jobs.js and estimates.js each had their own copy before this.
export function saveBlob(data, filename, mimeType) {
  const objectUrl = URL.createObjectURL(new Blob([data], { type: mimeType }));
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function apiClient(method, url, data, options = {}) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated");
  }

  const token = await user.getIdToken();

  const config = {
    method,
    url: `${API_BASE}${url}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // "View As" — see utils/viewAs.js and components/ViewAsBanner.jsx. Attached
  // to EVERY request while active, which is what makes the effective identity
  // change everywhere at once rather than page by page. The backend
  // (firebase_auth.get_current_user) re-validates on every single request
  // that the REAL, token-verified caller is a system_admin before it honours
  // this — the header alone grants nothing.
  const viewAsUserId = getViewAsUserId();
  if (viewAsUserId) {
    config.headers["X-View-As-User-Id"] = viewAsUserId;
  }

  // Optional axios overrides (e.g. { responseType: "blob" } for binary downloads)
  if (options.responseType) {
    config.responseType = options.responseType;
  }
  // An upload with no timeout can hang forever when the response is lost
  // (cellular drop, iOS suspending the tab) even though the server saved it.
  if (options.timeout) {
    config.timeout = options.timeout;
  }
  if (options.onUploadProgress) {
    config.onUploadProgress = options.onUploadProgress;
  }

  // Don't set Content-Type for FormData - let browser set it with boundary
  if (data instanceof FormData) {
    config.data = data;
    // Don't set Content-Type header - browser will set it with boundary
  } else {
    config.data = data;
    config.headers["Content-Type"] = "application/json";
  }

  try {
    return await axios(config);
  } catch (err) {
    // Only this one detail ejects. An ordinary 403 ("Permission denied") is a
    // normal response to a route the role cannot reach and must fall through
    // to the caller untouched.
    if (
      err?.response?.status === 403 &&
      err.response.data?.detail === ACCOUNT_ARCHIVED_DETAIL
    ) {
      ejectArchivedUser();
    }
    if (
      err?.response?.status === 401 &&
      err.response.data?.detail === PARTNER_SESSION_ENDED_DETAIL
    ) {
      ejectArchivedUser("Your partner sign-in has ended. Sign in again with the current password.");
    }
    throw err;
  }
}
